import type { Bonus, InventoryItem } from '../types/domain'
import { gameSupportsFreeBets } from './gameSupportsFreeBets'
import { groupFreeBets, type FreeBetGroup } from './groupFreeBets'
import { selectFreeBets } from './selectFreeBets'

export interface FreeBetAutoClaimPlan {
  /** Claimable free bets to claim now (excluding ids already attempted). */
  toClaim: Bonus[]
  /** One group per aggregated grant banner. */
  groups: FreeBetGroup[]
}

/**
 * Decides what to auto-claim and which banners to show for the current
 * inventory snapshot. Pure so the timing/effect wrapper stays trivial: the hook
 * coalesces a grant burst, then calls this once.
 */
export function planFreeBetAutoClaim(
  items: InventoryItem[],
  gameId: string | undefined,
  alreadyAttempted: Set<string>,
  gameType?: string
): FreeBetAutoClaimPlan {
  // v12.4 ask 6: never auto-claim (or banner) while the player is on a game
  // that cannot WAGER free bets — the backend hard-rejects them on crash, so
  // the grant banner would announce an unplayable reward. The items stay
  // `claimable` and auto-claim normally the next time the player opens an
  // applicable, free-bet-capable game.
  if (!gameSupportsFreeBets(gameType)) {
    return { toClaim: [], groups: [] }
  }

  const toClaim = selectFreeBets(items, { status: 'claimable', gameId }).filter(
    (item) => !alreadyAttempted.has(item.id)
  )

  return { toClaim, groups: groupFreeBets(toClaim) }
}
